// PasswordStrength: how strong a new password is, as a bar in a well and the word beside it, so
// the colour never carries the reading alone. docs/operations/design-standard.md, "Controls".

import React from 'react';

export type StrengthWord = 'Weak' | 'Fair' | 'Good' | 'Strong';

export interface Strength {
  /** 0 for nothing typed, then 1 to 4. */
  level: 0 | 1 | 2 | 3 | 4;
  word: StrengthWord | '';
}

/** One point each for 6 and 8 characters, a capital, a digit and a symbol. */
export const scorePassword = (password: string): Strength => {
  if (!password) return { level: 0, word: '' };
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (score <= 2) return { level: 1, word: 'Weak' };
  if (score <= 3) return { level: 2, word: 'Fair' };
  if (score <= 4) return { level: 3, word: 'Good' };
  return { level: 4, word: 'Strong' };
};

const TONE: Record<StrengthWord, { fill: string; text: string }> = {
  Weak: { fill: 'bg-red-400', text: 'text-red-300' },
  Fair: { fill: 'bg-amber-400', text: 'text-amber-300' },
  Good: { fill: 'bg-sky-400', text: 'text-sky-300' },
  Strong: { fill: 'bg-green-400', text: 'text-green-300' },
};

export interface PasswordStrengthProps {
  password: string;
  /** The rule under the bar ("At least 6 characters"). */
  hint?: string;
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ password, hint }) => {
  const { level, word } = scorePassword(password);
  const tone = word ? TONE[word] : null;
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <div aria-hidden="true" className="relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-inner bg-surface-container-high shadow-recess">
          <div
            className={`absolute inset-0 origin-left rounded-inner transition-[transform,background-color] duration-spring ease-spring motion-reduce:transition-[background-color] ${tone ? tone.fill : 'bg-surface-container-highest'}`}
            style={{ transform: `scaleX(${level / 4})` }}
          />
        </div>
        {/* The slot holds the widest word, so the bar never changes length as the word does. */}
        <span className="grid flex-none text-xs font-medium tabular-nums">
          <span aria-hidden="true" className="invisible col-start-1 row-start-1">Strong</span>
          <span aria-live="polite" className={`col-start-1 row-start-1 text-right ${tone ? tone.text : ''}`}>
            {word ? <><span className="sr-only">Strength: </span>{word}</> : null}
          </span>
        </span>
      </div>
      {hint ? <p className="m-0 mt-1 text-xs text-on-surface-variant">{hint}</p> : null}
    </div>
  );
};

export default PasswordStrength;
